// "What changed?" board for the V2 Business policy-change form.
//
// Renders a blue header bar and a responsive grid of change categories, each
// with small action buttons (Add / Change / Delete). Clicking a button asks
// the parent to open the relevant section form in a right-side drawer (or the
// record panel directly), and reports the underlying `sub_type` (and optional
// section mode) so the parent can keep Formik data + validation in sync.

import React from "react";
import { Grid, Paper, Stack, Typography } from "@mui/material";
import { SectionPanel } from "./SectionPanel";

// Section keys map to the real Business policy-change step groups.
export type ChangeSectionKey =
  | "nameChange"
  | "addressChange"
  | "riskLocationChange"
  | "coverageChange"
  | "interestedPartyChange"
  | "equipmentChange"
  | "toolFloaterChange"
  | "fineArtChange"
  | "paymentMethodChange"
  | "cancellationReinstatement"
  | "otherRequestType";

// The chip label shown on a category button. For most sections this is the
// action mode (Add/Change/Delete/Cancel/Reinstate); the "Others" card uses
// request-type labels (LOA/LOE/Other) instead.
export type ChangeActionMode =
  | "Add"
  | "Change"
  | "Delete"
  | "Cancel"
  | "Reinstate"
  | "LOA"
  | "LOE"
  | "Other"
  | "New"
  | "Remarket";

export interface IChangeSelection {
  section: ChangeSectionKey;
  // The value written to values.sub_type
  subType: string;
  // Optional mode (Add/Change/Delete) for record-based sections.
  mode?: ChangeActionMode;
  // For the "Others" card only: the request `type` code to set on the form
  // (LOA / LOE / GeneralChange). These are type-level requests in v1 that
  // carry no dedicated field form, so selecting one records the request type
  // and surfaces it in "Changes Requested" without opening a panel.
  requestType?: string;
  // Human label for the drawer heading
  heading: string;
}

interface IChangeAction {
  label: ChangeActionMode;
  color: "success" | "warning" | "error";
  selection: IChangeSelection;
}

interface IChangeCategory {
  title: string;
  hint?: string;
  section: ChangeSectionKey;
  actions: IChangeAction[];
}

// Helper to build the standard Add/Change/Delete action set for a
// record-based section, keeping the CATEGORIES array compact.
const recordActions = (
  section: ChangeSectionKey,
  subType: string,
  heading: string
): IChangeAction[] => [
  {
    label: "Add",
    color: "success",
    selection: { section, subType, mode: "Add", heading },
  },
  {
    label: "Change",
    color: "warning",
    selection: {
      section,
      subType,
      mode: "Change",
      heading: `${heading.replace(" Change", "")} Substitution`,
    },
  },
  {
    label: "Delete",
    color: "error",
    selection: { section, subType, mode: "Delete", heading },
  },
];

const CATEGORIES: IChangeCategory[] = [
  {
    title: "Named Insured",
    section: "nameChange",
    actions: [
      {
        label: "Change",
        color: "warning",
        selection: {
          section: "nameChange",
          subType: "Name Change",
          heading: "Name Change",
        },
      },
    ],
  },
  {
    title: "Address",
    section: "addressChange",
    actions: [
      {
        label: "Change",
        color: "warning",
        selection: {
          section: "addressChange",
          subType: "Address Change",
          heading: "Address Change",
        },
      },
    ],
  },
  {
    title: "Risk Location",
    section: "riskLocationChange",
    actions: recordActions(
      "riskLocationChange",
      "Risk Location Change",
      "Risk Location Change"
    ),
  },
  {
    title: "Coverage",
    section: "coverageChange",
    actions: recordActions("coverageChange", "Coverage Change", "Coverage Change"),
  },
  {
    title: "Interested Party",
    section: "interestedPartyChange",
    actions: recordActions(
      "interestedPartyChange",
      "Interested Party Change",
      "Interested Party Change"
    ),
  },
  {
    title: "Equipment",
    section: "equipmentChange",
    actions: recordActions(
      "equipmentChange",
      "Equipment Change",
      "Equipment Change"
    ),
  },
  {
    title: "Tool Floater",
    section: "toolFloaterChange",
    actions: recordActions(
      "toolFloaterChange",
      "Tool Floater Change",
      "Tool Floater Change"
    ),
  },
  {
    title: "Fine Art",
    section: "fineArtChange",
    actions: recordActions("fineArtChange", "Fine Art Change", "Fine Art Change"),
  },
  {
    title: "Payment Method",
    section: "paymentMethodChange",
    actions: [
      {
        label: "Change",
        color: "warning",
        selection: {
          section: "paymentMethodChange",
          subType: "Payment Method Change",
          heading: "Payment Method Change",
        },
      },
    ],
  },
  {
    title: "Cancellation / Reinstatement",
    hint: "Policy",
    section: "cancellationReinstatement",
    actions: [
      {
        label: "Cancel",
        color: "error",
        selection: {
          section: "cancellationReinstatement",
          subType: "Cancellation/Reinstatement",
          mode: "Cancel",
          heading: "Cancellation / Reinstatement",
        },
      },
      {
        label: "Reinstate",
        color: "success",
        selection: {
          section: "cancellationReinstatement",
          subType: "Cancellation/Reinstatement",
          mode: "Reinstate",
          heading: "Cancellation / Reinstatement",
        },
      },
    ],
  },
  {
    title: "Others",
    hint: "Doc",
    section: "otherRequestType",
    actions: [
      {
        label: "LOA",
        color: "success",
        selection: {
          section: "otherRequestType",
          subType: "LOA",
          requestType: "LOA",
          heading: "LOA",
        },
      },
      {
        label: "LOE",
        color: "success",
        selection: {
          section: "otherRequestType",
          subType: "LOE",
          requestType: "LOE",
          heading: "LOE",
        },
      },
      {
        label: "Other",
        color: "error",
        selection: {
          section: "otherRequestType",
          subType: "General Change",
          requestType: "GeneralChange",
          heading: "Other",
        },
      },
    ],
  },
  {
    title: "Upload",
    hint: "Doc",
    section: "otherRequestType",
    actions: [
      {
        label: "New",
        color: "success",
        selection: {
          section: "otherRequestType",
          subType: "Business (Upload)",
          requestType: "New",
          heading: "New (upload)",
        },
      },
    ],
  },
];

// Solid clickable chip (rendered as a Stack, not a MUI Button, so its size is
// fully controlled). Dark colored background with light text, compact rounded
// pill shape.
const chipSx = (color: IChangeAction["color"], disabled: boolean) =>
  (theme: any) => {
    const dark = theme.palette[color].dark || theme.palette[color].main;
    const main = theme.palette[color].main;
    // The chip background always uses the palette's deep `dark` shade, so
    // white text stays legible for every color. (The theme's own
    // contrastText for some palettes — e.g. `info` — is dark and renders
    // poorly on the dark background.)
    const contrast = theme.palette.common.white;
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 62,
      height: 24,
      px: 1.25,
      borderRadius: "12px",
      fontWeight: 700,
      fontSize: "0.72rem",
      lineHeight: 1,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap" as const,
      userSelect: "none" as const,
      cursor: disabled ? "not-allowed" : "pointer",
      color: contrast,
      backgroundColor: dark,
      border: `1px solid ${dark}`,
      opacity: disabled ? 0.5 : 1,
      transition: "background-color 0.15s ease, border-color 0.15s ease",
      "&:hover": disabled
        ? {}
        : {
            backgroundColor: main,
            borderColor: main,
          },
    };
  };

interface IWhatChangedBoardProps {
  activeSubType?: string | null;
  disabled?: boolean;
  onSelect: (selection: IChangeSelection) => void;
}

export const WhatChangedBoard: React.FC<IWhatChangedBoardProps> = ({
  activeSubType,
  disabled = false,
  onSelect,
}) => {
  // Always show every category. The Subtype field was removed from the form,
  // so the board no longer filters by sub_type — all change categories are
  // available at once.
  const visibleCategories = CATEGORIES;

  return (
    <SectionPanel
      heading="What changed?"
      rightText="One request carries every change"
      sx={{ mt: 2, mb: 1 }}
    >
      <Grid container spacing={2}>
        {visibleCategories.map((category) => {
          const isActive = category.actions.some(
            (a) => a.selection.subType === activeSubType
          );
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={category.title}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderColor: isActive ? "primary.main" : "divider",
                  boxShadow: isActive ? 2 : 0,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  mb={1}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {category.title}
                  </Typography>
                  {category.hint && (
                    <Typography variant="caption" color="text.disabled">
                      {category.hint}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {category.actions.map((action) => (
                    <Stack
                      key={`${category.title}-${action.label}`}
                      component="span"
                      role="button"
                      tabIndex={disabled ? -1 : 0}
                      aria-disabled={disabled}
                      sx={chipSx(action.color, disabled)}
                      onClick={() => {
                        if (!disabled) {
                          onSelect(action.selection);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (!disabled && (e.key === "Enter" || e.key === " ")) {
                          e.preventDefault();
                          onSelect(action.selection);
                        }
                      }}
                    >
                      {action.label}
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </SectionPanel>
  );
};
